/**
 * @fileoverview Molecule: the celebratory relaunch shown on a crossed tier.
 * @module components/molecules/MilestoneToast
 *
 * @description
 * The "relance" the goal asks for: a habit crossing a milestone tier (1, 15,
 * 33, 66, 100, ...) gets a short, self-dismissing celebration rather than a
 * silent counter bump.
 */

import { useEffect } from "react"

import { formatMilestone } from "@/lib/domain/milestones"

import "./MilestoneToast.css"

/** Props accepted by {@link MilestoneToast}. */
export interface MilestoneToastProps {
	/** Name of the habit that crossed a tier. */
	readonly habitName: string
	/** Tiers crossed by the log that triggered this toast, ascending. */
	readonly milestones: readonly number[]
	/** Called once the toast should disappear. */
	readonly onDismiss: () => void
}

/** How long the toast stays up before dismissing itself. */
const VISIBLE_MS = 4000

/**
 * Self-dismissing celebration for one or more milestone tiers just reached.
 *
 * @param props - The habit, the tiers crossed and the dismiss callback.
 * @returns The rendered toast.
 */
export function MilestoneToast({
	habitName,
	milestones,
	onDismiss,
}: MilestoneToastProps) {
	useEffect(() => {
		const timer = window.setTimeout(onDismiss, VISIBLE_MS)
		return () => {
			window.clearTimeout(timer)
		}
	}, [onDismiss])

	const tiers = milestones.map(formatMilestone).join(", ")

	return (
		<div className="milestone-toast" role="status">
			<span className="milestone-toast__icon" aria-hidden="true">
				🎉
			</span>
			<p className="milestone-toast__message">
				<strong>{habitName}</strong> a atteint le palier {tiers} !
			</p>
		</div>
	)
}
